import "core-js/modules/web.url-search-params.delete.js";
import "core-js/modules/web.url-search-params.has.js";
import "core-js/modules/web.url-search-params.size.js";
import UserApi from 'app/api/UserApi';
import { isViewOnlyProduct } from 'app/types/Product';
import cx from 'classnames';
import { track } from "../core/App/track";
import { CtaButton } from "../core/components/CtaButton";
import CONFIG from "../core/config";
import { TrialBadge } from 'legoland-sdk';
import { AccessType } from 'legoland-sdk/dist/experimental';
import { HStack, VStack } from 'llui/dist/Stack';
import { Spacer } from 'llui/dist/Stack/Spacer';
import { ProductLogo } from "../products/components/ProductLogo";
import { useContext, useEffect, useRef, useState } from 'react';
import { FormattedDate } from 'react-intl';
import { Button, Text, TooltipOnHover, tombac } from 'tombac';
import { GuideIcon, LimitsIcon, WarningIcon, WhatsNewIcon } from 'tombac-icons';
import DashboardProductsContext from "./DashboardProductsContext";
import "./ProductCard.css";
import TryForFreeModal from "./TryForFreeModal";
import LimitUsageModal from "./limits/LimitUsageModal";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const productsWithUsage = ['od', 'ctm', 'ts', 'ja', 'aa'];
function AccessInfo({
  access
}) {
  if (access.status === 'PENDING') {
    return /*#__PURE__*/_jsx("div", {
      className: "ProductCard__access-info",
      children: "Pending access"
    });
  }
  if (access.status === 'EXPIRED') {
    return /*#__PURE__*/_jsxs("div", {
      className: "ProductCard__access-info",
      children: ["Expired on", ' ', /*#__PURE__*/_jsx(FormattedDate, {
        value: new Date(access.expiryDate),
        year: "numeric",
        month: "short",
        day: "2-digit"
      })]
    });
  }
  if (access.status === 'GRANTED') {
    return /*#__PURE__*/_jsxs("div", {
      className: "ProductCard__access-info",
      children: ["Expires on", ' ', /*#__PURE__*/_jsx(FormattedDate, {
        value: new Date(access.expiryDate),
        year: "numeric",
        month: "short",
        day: "2-digit"
      })]
    });
  }
  return null;
}
function DaysRemaining({
  access
}) {
  if (access.status === 'GRANTED') {
    const expiryDate = new Date(access.expiryDate).getTime();
    const now = Date.now();
    const DAY_IN_MS = 24 * 60 * 60 * 1000;
    const diff = expiryDate - now;
    const days = Math.ceil(diff / DAY_IN_MS);
    const FEW_DAYS_THRESHOLD = 30;
    if (days > FEW_DAYS_THRESHOLD) {
      return null;
    }
    return /*#__PURE__*/_jsxs("div", {
      className: "ProductCard__days-remaining",
      children: [days, " days remaining"]
    });
  }
  return null;
}
function TryForFree({
  product,
  userEmail
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAccountBeingCreated, setIsAccountBeingCreated] = useState(false);
  const {
    products,
    reloadProducts
  } = useContext(DashboardProductsContext);
  const intervalIdRef = useRef(undefined);
  const requestCountRef = useRef(0);
  const TRIAL_CREATION_POLL_INTERVAL = 1500;
  const MAX_TRY_FOR_FREE_UPDATE_PRODUCT_REQUEST_COUNT = 5;
  const updateProducts = async () => {
    await reloadProducts();
    const triedProduct = products.find(p => p.id === product.id);
    const isProductAccessGranted = triedProduct.access.status === 'GRANTED';
    return isProductAccessGranted;
  };
  const startTrial = async () => {
    setIsAccountBeingCreated(true);
    await UserApi.tryForFree(product.id);
    track('form_submit', {
      category: 'dashboard',
      specificProps: {
        form_type: 'mp_trail',
        visitor_type: 'known',
        account_type: 'move',
        staticId: Buffer.from(userEmail).toString('base64')
      }
    });
    intervalIdRef.current = setInterval(async () => {
      const isProductAccessGranted = await updateProducts();
      requestCountRef.current += 1;
      if (isProductAccessGranted === true || requestCountRef.current >= MAX_TRY_FOR_FREE_UPDATE_PRODUCT_REQUEST_COUNT) {
        setIsAccountBeingCreated(false);
        setIsModalOpen(false);
        clearInterval(intervalIdRef.current);
      }
    }, TRIAL_CREATION_POLL_INTERVAL);
  };
  useEffect(() => {
    return () => {
      clearInterval(intervalIdRef.current);
    };
  }, []);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [isModalOpen && /*#__PURE__*/_jsx(TryForFreeModal, {
      product: product,
      isAccountBeingCreated: isAccountBeingCreated,
      onClose: () => {
        setIsModalOpen(false);
      },
      onConfirm: () => {
        startTrial();
      }
    }), /*#__PURE__*/_jsx(Button, {
      busy: isAccountBeingCreated,
      variant: "alert",
      onClick: () => setIsModalOpen(true),
      "data-sentry-element": "Button",
      "data-sentry-source-file": "ProductCard.tsx",
      "data-sentry-element": "Button",
      children: "Try for free"
    })]
  });
}
function ProductCardActions({
  product,
  userEmail
}) {
  const [showLimits, setShowLimits] = useState(false);
  const [hasLimits, setHasLimits] = useState(false);
  const canLaunch = product.access.status === 'GRANTED' || product.access.status === 'PENDING';
  const userGuides = {
    ctm: 'https://developer.tomtom.com/move-portal/guides/route-monitoring/introduction',
    ja: 'https://developer.tomtom.com/move-portal/guides/junction-analytics/introduction',
    od: 'https://developer.tomtom.com/move-portal/guides/od-analysis/introduction',
    ts: 'https://developer.tomtom.com/move-portal/guides/traffic-stats/introduction'
  };
  const releaseNotesUrl = product.access.status === 'GRANTED' && ['ja', 'od', 'rer', 'ts', 'ctm'].includes(product.id) ? new URL('release-notes', product.url).href : undefined;
  const canHaveLimits = productsWithUsage.includes(product.id);
  const learnMoreLink = 'informationLink' in product && /*#__PURE__*/_jsx(CtaButton, {
    $marginLeft: "-2.5sp",
    as: "a",
    href: product.informationLink,
    onClick: () => {
      track('page_click', {
        category: 'dashboard',
        specificProps: {
          component_name: 'product-card',
          component_sub_name: 'learn-more-button',
          click_href: product.informationLink
        }
      });
    },
    target: "_blank",
    children: "Learn more"
  });
  if (product.access.status === 'DENIED') {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Spacer, {}), /*#__PURE__*/_jsxs(HStack, {
        children: [learnMoreLink, /*#__PURE__*/_jsx(Spacer, {}), CONFIG.EVALUATION[product.id] && CONFIG.EVALUATION[product.id].isEvaluationAvailable && /*#__PURE__*/_jsx(TryForFree, {
          product: product,
          userEmail: userEmail
        })]
      })]
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [userGuides[product.id] && /*#__PURE__*/_jsx(TooltipOnHover, {
      content: "Support center",
      placement: "top",
      size: "s",
      variant: "inverted",
      children: /*#__PURE__*/_jsx(Button, {
        variant: "flat",
        as: "a",
        href: userGuides[product.id],
        onClick: () => {
          track('page_click', {
            category: 'dashboard',
            specificProps: {
              component_name: 'product-card',
              component_sub_name: 'support-center-button',
              click_href: userGuides[product.id]
            }
          });
        },
        shape: "circle",
        $mr: "0.5sp",
        children: /*#__PURE__*/_jsx(GuideIcon, {})
      })
    }), releaseNotesUrl && /*#__PURE__*/_jsx(TooltipOnHover, {
      content: "Release notes",
      placement: "top",
      size: "s",
      variant: "inverted",
      children: /*#__PURE__*/_jsx(Button, {
        variant: "flat",
        as: "a",
        href: releaseNotesUrl,
        onClick: () => {
          track('page_click', {
            category: 'dashboard',
            subcategory: 'release-notes',
            specificProps: {
              component_name: 'product-card',
              component_sub_name: 'button',
              click_href: releaseNotesUrl
            }
          });
        },
        shape: "circle",
        $mr: "0.5sp",
        children: /*#__PURE__*/_jsx(WhatsNewIcon, {})
      })
    }), canHaveLimits && product.access.status === 'GRANTED' && /*#__PURE__*/_jsx(LimitUsageModal, {
      isOpen: showLimits,
      onRequestClose: () => setShowLimits(false),
      onHasLimits: () => setHasLimits(true),
      product: product
    }), hasLimits && /*#__PURE__*/_jsx(TooltipOnHover, {
      content: "Usage limits",
      placement: "top",
      size: "s",
      variant: "inverted",
      children: /*#__PURE__*/_jsx(Button, {
        variant: "flat",
        onClick: () => {
          track('page_click', {
            category: 'dashboard',
            subcategory: 'limits',
            specificProps: {
              component_name: 'product-card',
              component_sub_name: 'button',
              click_href: product.url
            }
          });
          setShowLimits(true);
        },
        shape: "circle",
        className: "ProductCard__limits",
        children: /*#__PURE__*/_jsx(LimitsIcon, {})
      })
    }), /*#__PURE__*/_jsx(Spacer, {
      "data-sentry-element": "Spacer",
      "data-sentry-source-file": "ProductCard.tsx",
      "data-sentry-element": "Spacer"
    }), canLaunch ? /*#__PURE__*/_jsx(Button, {
      as: "a",
      busy: product.access.status === 'PENDING',
      href: product.url,
      variant: "primary",
      onClick: () => {
        track('page_click', {
          category: 'dashboard',
          specificProps: {
            component_name: 'product-card',
            component_sub_name: 'launch-button',
            click_href: product.url
          }
        });
      },
      children: "Launch"
    }) : learnMoreLink]
  });
}
const DecommissionLabel = ({
  productId
}) => productId === 'rer' ? /*#__PURE__*/_jsx(_Fragment, {
  children: 'Decommissions soon'
}) : /*#__PURE__*/_jsxs(_Fragment, {
  children: ["Decommissions on", ' ', /*#__PURE__*/_jsx(FormattedDate, {
    value: new Date('2025-04-11'),
    year: "numeric",
    month: "short",
    day: "2-digit"
  })]
});
function ProductCardContent({
  product,
  trial
}) {
  const productDescription = {
    aa: {
      tagline: 'Analyze regions',
      description: 'Choose areas and timeframes to compare traffic data over time, gain immediate insights into patterns and trends, and benchmark across regions, with a wealth of data.'
    },
    ts: {
      tagline: 'Find bottlenecks',
      description: 'Analyze, predict and improve traffic flow with road network analysis and bottleneck intel'
    },
    od: {
      tagline: 'Understand mobility',
      description: 'For trip dynamics: highly advanced technology combined with great data visualization'
    },
    rer: {
      tagline: 'Report road closures',
      description: 'Report road closures, road works, accidents, and other traffic disruptions in your area'
    },
    ctm: {
      tagline: 'Inform drivers',
      description: 'Closely monitor traffic and travel times on custom routes and broadcast messages to drivers in real traffic'
    },
    ja: {
      tagline: 'Analyze Intersections',
      description: 'Monitor junction turn ratios, current delay, and other valuable traffic information based on gathered real-time traffic data'
    }
  };
  const details = productDescription[product.id];
  if (isViewOnlyProduct(product)) {
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsxs(HStack, {
        spacing: "xxl",
        style: {
          height: '48px'
        },
        children: [/*#__PURE__*/_jsx("div", {
          className: "ProductCard__logo",
          children: /*#__PURE__*/_jsx(ProductLogo, {
            productId: product.id
          })
        }), /*#__PURE__*/_jsx("div", {
          className: "ProductCard__title",
          children: product.displayName
        })]
      }), details && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("div", {
          className: "ProductCard__tagline",
          children: details.tagline
        }), /*#__PURE__*/_jsx("p", {
          className: "ProductCard__description",
          children: details.description
        })]
      })]
    });
  }
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsxs(HStack, {
      style: {
        height: '48px'
      },
      spacing: "xxl",
      "data-sentry-element": "HStack",
      "data-sentry-source-file": "ProductCard.tsx",
      "data-sentry-element": "HStack",
      children: [/*#__PURE__*/_jsx("div", {
        className: "ProductCard__logo",
        children: /*#__PURE__*/_jsx(ProductLogo, {
          productId: product.id,
          "data-sentry-element": "ProductLogo",
          "data-sentry-source-file": "ProductCard.tsx",
          "data-sentry-element": "ProductLogo"
        })
      }), /*#__PURE__*/_jsxs(VStack, {
        spacing: "s",
        "data-sentry-element": "VStack",
        "data-sentry-source-file": "ProductCard.tsx",
        "data-sentry-element": "VStack",
        children: [/*#__PURE__*/_jsxs("div", {
          className: "ProductCard__title",
          children: [product.displayName, ' ', trial && /*#__PURE__*/_jsx(TrialBadge, {
            $display: "inline-flex",
            $ml: "0.5sp",
            $verticalAlign: "3u"
          })]
        }), product.id === 'rer' || product.id === 'tv' ? /*#__PURE__*/_jsxs(Text, {
          $color: tombac.color('primary', 500),
          children: [/*#__PURE__*/_jsx(WarningIcon, {
            size: 'xs'
          }), ' ', /*#__PURE__*/_jsx(DecommissionLabel, {
            productId: product.id
          })]
        }) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(AccessInfo, {
            access: product.access
          }), /*#__PURE__*/_jsx(DaysRemaining, {
            access: product.access
          }), ' ']
        })]
      })]
    })
  });
}
export function MoreAboutTomTomAnalyticsCard() {
  const TOMTOM_COM_ROAD_ANALYTICS_URL = 'https://www.tomtom.com/products/tomtom-move/';
  const learnMoreLink = /*#__PURE__*/_jsx(CtaButton, {
    $margin: "0 -2.5sp",
    as: "a",
    href: TOMTOM_COM_ROAD_ANALYTICS_URL,
    onClick: () => {
      track('page_click', {
        category: 'dashboard',
        specificProps: {
          component_name: 'product-card',
          component_sub_name: 'learn-more-button',
          click_href: TOMTOM_COM_ROAD_ANALYTICS_URL
        }
      });
    },
    target: "_blank",
    children: "Learn more"
  });
  return /*#__PURE__*/_jsxs("div", {
    className: "ProductCard",
    "data-sentry-component": "MoreAboutTomTomAnalyticsCard",
    "data-sentry-source-file": "ProductCard.tsx",
    children: [/*#__PURE__*/_jsx("div", {
      className: "ProductCard__content",
      children: /*#__PURE__*/_jsxs("div", {
        className: "ProductCard__title",
        children: ["More info about TomTom road traffic data analytics", ' ']
      })
    }), /*#__PURE__*/_jsx("div", {
      className: "ProductCard__actions",
      children: learnMoreLink
    })]
  });
}
export function ProductCard({
  product,
  userEmail
}) {
  const trial = 'isFallback' in product && product.isFallback === true ? false : (product.access.status === 'GRANTED' || product.access.status === 'EXPIRED' || product.access.status === 'PENDING') && product.access.accessType === AccessType.Evaluation;
  const className = cx('ProductCard', {
    'ProductCard--expired': product.access.status === 'EXPIRED',
    'ProductCard--trial': trial
  });
  return /*#__PURE__*/_jsxs("div", {
    className: className,
    "data-sentry-component": "ProductCard",
    "data-sentry-source-file": "ProductCard.tsx",
    children: [/*#__PURE__*/_jsx("div", {
      className: "ProductCard__content",
      children: /*#__PURE__*/_jsx(ProductCardContent, {
        product: product,
        trial: trial,
        "data-sentry-element": "ProductCardContent",
        "data-sentry-source-file": "ProductCard.tsx",
        "data-sentry-element": "ProductCardContent"
      })
    }), /*#__PURE__*/_jsx("div", {
      className: "ProductCard__actions",
      children: /*#__PURE__*/_jsx(ProductCardActions, {
        product: product,
        userEmail: userEmail,
        "data-sentry-element": "ProductCardActions",
        "data-sentry-source-file": "ProductCard.tsx",
        "data-sentry-element": "ProductCardActions"
      })
    })]
  });
}